import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { AppDispatch, RootState, useAppSelector } from "../../../redux/store";
import Board from "../../trello-board/Board";
import {
  moveProviderCardToAnotherBoard,
} from "../../../redux/features/providers-main/provider-board-slice";
import { OrgBU } from "../../../types/company";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Board as IBoard, BoardCard, stages } from "src/types/board";
import { setBusinessUnitFilters } from "src/redux/features/custom-board/custom-board-slice";
import { GetAllProviders } from "src/redux/features/providers-main/provider-list-slice";
import Loader from "../../../layouts/loader/Loader";
import { updateOnBoardingStage } from "src/redux/features/providers-main/provider-onboardingstage-slice";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
const Boards = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: baseline;
`;

interface ProviderBoardProps {
  callbacks: ((id: string) => Promise<void>)[];
  chatCommand: (id: string) => void;
}

const BoardStage: React.FC<ProviderBoardProps> = ({
  callbacks,
  chatCommand,
}) => {
  // #region variables region
  //dispatch for redux
  const dispatch = useDispatch<AppDispatch>();

  //intake stages
  const initOnBoardingStages = useSelector<RootState, IBoard>(
    (state) => state.providerOnBoardingStage
  );

  const [onBoardingStages, setOnBoardingStages] = useState<stages[]>(
    initOnBoardingStages.stages
  );

  //get all providers from redux
  const providerList = useAppSelector(
    (state) => state.providerListSlice.listData
  );
  //get loading status for getAllproviders api
  const loader = useAppSelector((state) => state.providerListSlice.loader);

  const businessUnitsFilterDetails = useAppSelector(
    (state) => state.boardSlice.selectedBusinessUnits
  );

  const selectedBusinessUnits =
    businessUnitsFilterDetails?.find((item) => item.boardId === "provider")
      ?.businessUnits || [];

  const businessUnits = useAppSelector(
    (state) => state.businessUnitsSlice.businessUnits
  );
  // #endregion

  // #region methods region
  const removeFocus = (id: string) => {
    document.getElementById(id)?.blur();
  };

  const handleDragEnd = ({ source, destination, draggableId }: DropResult) => {
    if (!destination) return;

    const { droppableId: sourceStageId, index: sourceIndex } = source;
    const { droppableId: destinationStageId, index: destinationIndex } =
      destination;

    const sourceStage = onBoardingStages.find(
      (s) => s.id === sourceStageId
    );
    const destinationStage = onBoardingStages.find(
      (s) => s.id === destinationStageId
    );

    if (!sourceStage || !destinationStage) return;

    const updatedStages = onBoardingStages.map((stage) => {
      // Same stage reordering
      if (sourceStageId === destinationStageId && stage.id === sourceStage.id) {
        const updatedCards = [...stage.boardColumnNames];
        const [movedCard] = updatedCards.splice(sourceIndex, 1);
        updatedCards.splice(destinationIndex, 0, movedCard);
        return { ...stage, boardColumnNames: updatedCards };
      }

      // Cross-stage move: remove from source
      if (stage.id === sourceStage.id) {
        const updatedCards = [...stage.boardColumnNames];
        updatedCards.splice(sourceIndex, 1);
        return { ...stage, boardColumnNames: updatedCards };
      }

      // Cross-stage move: insert into destination
      if (stage.id === destinationStage.id) {
        const updatedCards = [...stage.boardColumnNames];
        updatedCards.splice(
          destinationIndex,
          0,
          sourceStage.boardColumnNames[sourceIndex]
        );
        return { ...stage, boardColumnNames: updatedCards };
      }

      return stage;
    });

    // Update Redux once
    dispatch(
      updateOnBoardingStage({
        updatedStages: updatedStages,
        boardId: initOnBoardingStages.id,
      })
    );

    // API call once
    dispatch(
      moveProviderCardToAnotherBoard({
        fromBoardId: sourceStageId,
        toBoardId: destinationStageId,
        cardId: draggableId,
        index: destinationIndex,
      })
    );
  };

  useEffect(() => {
    setOnBoardingStages(initOnBoardingStages.stages);
  }, [initOnBoardingStages, initOnBoardingStages?.stages]);

  // fetch all providers
  useEffect(() => {
    if (providerList.length === 0) {
      dispatch(GetAllProviders("full"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerList?.length]);

  //business unit filtering
useEffect(() => {
  if (!providerList.length || !onBoardingStages.length) return;

  const shouldFilter = selectedBusinessUnits.length > 0;

  if (shouldFilter) {
    const selectedBUIds = new Set(selectedBusinessUnits.map((bu: OrgBU) => bu.id));

    const filteredProviderIds = new Set(
      providerList
        .filter((provider) =>
          provider.employmentDetails.businessUnit.some((buId: string) => selectedBUIds.has(buId))
        )
        .map((provider) => provider.employmentDetails.id) // assuming each provider has a unique entityId
    );

    const filteredStages = onBoardingStages.map((stage) => ({
      ...stage,
      boardColumnNames: stage.boardColumnNames.filter(
        (card: BoardCard) => card.entityId && filteredProviderIds.has(card.entityId)
      ),
    }));

    setOnBoardingStages(filteredStages);
  } else {
    setOnBoardingStages(initOnBoardingStages.stages);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [businessUnitsFilterDetails, providerList, onBoardingStages]);

  // #endregion

  return (
    <>
      <div className="taskHeader">
        <Typography>Filter:</Typography>
        <div className="taskFormController">
          <FormControl className="task-outer">
            <InputLabel id="demo-simple-select-label">
              {"Business Unit"}
            </InputLabel>
            <Select
              className="form-control bufilterDropdown"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              style={{ width: "250px" }}
              value={selectedBusinessUnits}
              onChange={(e) => {
                const selectedId = e.target.value;
                let updatedSelectedBU: OrgBU[] = [...selectedBusinessUnits];

                const isSelected = selectedBusinessUnits.some(
                  (item: OrgBU) => item.id === selectedId
                );

                if (isSelected) {
                  updatedSelectedBU = selectedBusinessUnits.filter(
                    (item: OrgBU) => item.id !== selectedId
                  );
                } else {
                  const newBUFilter = businessUnits.find(
                    (item) => item.id === selectedId
                  );
                  if (newBUFilter) {
                    updatedSelectedBU = [...selectedBusinessUnits, newBUFilter];
                  }
                }
                dispatch(
                  setBusinessUnitFilters({
                    boardId: "provider",
                    businessUnits: updatedSelectedBU,
                  })
                );
              }}
              renderValue={(selected: OrgBU[]) =>
                selected.map((b) => b.businessUnitName).join(", ")
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  removeFocus("demo-simple-select");
                }
              }}
              MenuProps={MenuProps}
            >
              {businessUnits?.map((item: OrgBU) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox
                    checked={selectedBusinessUnits.some(
                      (bu: OrgBU) => bu.id === item.id
                    )}
                  />
                  {item.businessUnitName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
      <Loader isShow={loader} />
      
        <Boards>
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="allStageCards allStageCardsBoard">
              {onBoardingStages.map((stage, index) => (
                <Board
                  key={stage.id}
                     stageId={stage.id}
                  stage={stage}
                  callbacks={callbacks}
                  chatCommand={chatCommand}
                />
              ))}
            </div>
          </DragDropContext>
        </Boards>

    </>
  );
};

export default BoardStage;
